import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ISO_COUNTRY_CODES } from '@goodbuild/rules/countries';
import { USERNAME_MAX, usernameProblem } from '@goodbuild/rules/username';
import { Link, Loader, ui } from '../ui';
import { signUp } from '../auth';
import { useApiMutation } from '../api';
import { AuthScreen, Field, column, returnTo } from './shared';
import { PROVIDERS, runProviderSignIn, useSignInOptions } from './providers';
import { FlowField } from '../ui/components/flow/fields';
import type { FlowChoiceOption, FlowStep } from '../ui/components/flow/types';
import styles from '../ui/components/flow/flow.module.css';

/**
 * Making an account, one question at a time.
 *
 * This was a single panel: name, email, password, three buttons underneath.
 * Everything that panel asked is still asked, and three things it never asked
 * are asked as well — where somebody is, whether they agree to the terms and
 * the privacy policy, and whether they are eighteen or older — which is what
 * turned it from a form into a walk. Five questions stacked in one column is
 * the shape people close.
 *
 * ## The order, which is the whole design
 *
 * 1. **The rules**, before anything else. Somebody who will not agree should
 *    find that out before they have typed a password, not after — and putting
 *    the documents first is the difference between asking and extracting. Each
 *    one opens in a tab of its own, so reading them does not cost the answers
 *    already given.
 * 2. **Where they are**, and it usually arrives already answered. The API
 *    guesses it from the connection, so what this step shows is one country
 *    and a *Change* beside it — the list of 249 only opens for the person the
 *    guess is wrong about. Still self-declared: a guess nobody carried on past
 *    is not a declaration, and the record is written at the end either way.
 * 3. **How they want to sign up** — every provider this deployment has, and an
 *    email address. This is the question that decides whether there are two
 *    more.
 * 4. **A username**, only on the email route. A provider hands us a name and
 *    asking for a second one would be a form in the middle of the flow whose
 *    whole appeal is that there is no form.
 * 5. **The email address and the password, together on one screen.** Every
 *    other step is one question, and this one is deliberately two: a password
 *    manager offers to save a credential when it sees an identifier and a
 *    password submitted by the same form, and splitting them across two screens
 *    is how you get a saved password with nothing attached to it.
 *
 * The provider route ends at step three: the popup comes back with a session
 * and there is nothing else to ask. The progress strip therefore shows three
 * bars until somebody picks email and five afterwards, exactly as a `when`
 * step does in the flow engine — a question that stopped applying stops
 * occupying a position.
 *
 * **The two routes end in different places, and that is the point of the
 * split.** A provider has verified the address itself, so those accounts land
 * in the product. An address and a password have proved nothing, so those land
 * on the wall at `/verify-email` and stay there until the link in the inbox is
 * clicked — see `finish`, and `requireCaller` in the API, which is what makes
 * that a fact about the account rather than a screen.
 *
 * ## Why this is not `<Flow>`
 *
 * It borrows that engine's stylesheet and its field renderer, and deliberately
 * not the engine. `Flow` posts a `FormData` to a server action and ends on a
 * review screen listing every answer with a `Change` beside it. Neither fits:
 * there is no action — `signUp.email` is a library call and a provider button
 * is an OAuth round trip — and the last answer here is a password, which is the
 * one answer that must never be printed back onto a review screen. What is
 * shared is what should be: the progress strip, the one-question-per-screen
 * shape, and `FlowField`, so the country filter here is the same control as the
 * country filter in the creator application rather than a second one that
 * behaves nearly the same.
 *
 * ## What is recorded, and the one gap in it
 *
 * `POST /v1/me/registration` writes the country, the agreement and the age
 * declaration against the account, once, after the session exists. Both routes
 * post the same three answers through the same endpoint — see the note there
 * for why this is a second call rather than part of the sign-up itself, and for
 * the window it leaves.
 *
 * **The gap: the sign-in page's provider buttons still create accounts.**
 * Pressing "Continue with Discord" on `/sign-in` with no account makes one, and
 * that account has been asked none of this. Closing it means either refusing
 * sign-up at those buttons or gating the whole application on a declaration
 * every existing account is also missing, and both are product decisions rather
 * than tidying.
 */
export function SignUpRoute(): React.ReactElement {
  const call = useApiMutation();
  const headingId = useId();

  const [index, setIndex] = useState(0);
  /** Which way the last move went, so the step animates in from that side. */
  const [goingBack, setGoingBack] = useState(false);

  const [agreed, setAgreed] = useState(false);
  const [ofAge, setOfAge] = useState(false);
  const [country, setCountry] = useState('');
  const [countryPickedByHand, setCountryPickedByHand] = useState(false);
  const [countryListOpen, setCountryListOpen] = useState(false);
  const [username, setUsername] = useState('');
  /**
   * Whether the email route was chosen, which is what makes steps 4 and 5 exist.
   *
   * A boolean rather than a `method` string holding `'email' | 'discord' | …`:
   * picking a provider is not an answer that is kept, it is an act that either
   * ends the flow or fails and leaves the question exactly as it was.
   */
  const [byEmail, setByEmail] = useState(false);

  /** The provider being waited on, `account` while the account is being made. */
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { available: offeredProviders, detectedCountry } = useSignInOptions();

  useEffect(() => {
    if (detectedCountry !== null && !countryPickedByHand) setCountry(detectedCountry);
  }, [detectedCountry, countryPickedByHand]);

  const asked = useMemo(() => (byEmail ? EMAIL_STEPS : PROVIDER_STEPS), [byEmail]);
  const step = asked[Math.min(index, asked.length - 1)]!;
  const working = busy !== null;

  const countryIsOnlyAGuess = country !== '' && !countryPickedByHand;
  const countryStepIsAnswered = step === 'country' && country !== '' && !countryListOpen;

  /**
   * A list that was open when somebody arrived stays open.
   *
   * The guess is normally in before this step is: it is fetched on mount and
   * the question before it is two documents and two checkboxes. But on a slow
   * connection — the one where the fetch is still out — somebody can reach this
   * step with no answer, get the list, and start typing. Collapsing the list
   * under them at the moment the guess lands would take the filter box away
   * mid-word and replace it with a country they did not choose.
   *
   * So arriving with nothing opens the list for as long as this step lives, and
   * a guess that turns up late is shown where a picked answer is shown: named
   * above the list, still theirs to accept or ignore.
   */
  useEffect(() => {
    if (step === 'country' && country === '') setCountryListOpen(true);
  }, [step, country]);

  function pickCountry(picked: string): void {
    setCountryPickedByHand(true);
    setCountryListOpen(false);
    setCountry(picked);
  }

  /**
   * Everything the account needs beyond a session, posted once it has one.
   *
   * **Tried twice, and a second failure is not allowed to strand anybody.** By
   * the time this runs the account exists and the person is signed in; refusing
   * to let them into a product they have just joined, over a record that is
   * ours to keep rather than theirs, would be punishing them for our network.
   * So it warns and moves on, which leaves the account in the same state as one
   * made through the sign-in page's provider buttons — see the gap noted above.
   */
  const record = useCallback(async (): Promise<void> => {
    const body = JSON.stringify({ countryCode: country, agreedToTerms: true, ageConfirmed: true });

    for (const attempt of [1, 2]) {
      try {
        await call('/me/registration', { method: 'POST', body });
        return;
      } catch (cause) {
        if (attempt === 2) {
          console.warn('The account was created but its declaration was not recorded.', cause);
        }
      }
    }
  }, [call, country]);

  /**
   * Out of the flow, to wherever this account is actually allowed to be.
   *
   * **Two endings, decided by whether the address is already proved.** A
   * provider hands back an address it has verified itself, so those accounts go
   * straight into the product. An account made with a password has proved
   * nothing yet: every route in the API refuses it until the link in its inbox
   * is clicked, so the only honest place to send it is the screen that says so.
   * Landing it in an empty library would be a product that appears to have let
   * somebody in and then refuses everything they touch.
   *
   * `/library` rather than the sign-in page's `site === 'creators' ? '/board'`,
   * because an account made a second ago is not an approved creator and the
   * board is closed until somebody decides it is open. `returnTo` still wins
   * where there is one — somebody who pressed *Buy* while signed out
   * came here to buy a game, not to look at an empty library. It is not
   * consulted on the unconfirmed path: that redirect has to survive an email
   * round trip, and `/verified` is where the link lands.
   *
   * A full navigation rather than a router push, like every other way into a
   * session: the whole app is behind it, and reloading is the simplest way to
   * be sure nothing rendered before it existed is still on screen.
   */
  const finish = useCallback(
    async (confirmed: boolean): Promise<void> => {
      await record();
      window.location.href = confirmed ? (returnTo() ?? '/library') : '/verify-email';
    },
    [record],
  );

  /**
   * On to the next question, if this one will let us.
   *
   * `skipCheck` is what a picked list item passes. A choice is a decision
   * rather than a draft, so picking one continues — and the value it picked is
   * not in state yet at the moment of the click, so asking `problemFor` about
   * the answers this render holds would judge the click against the state it
   * has just replaced. Every card step in the flow engine had that bug once;
   * this sidesteps it by not asking a question the pick has already answered.
   */
  const advance = useCallback(
    (skipCheck = false) => {
      const found = skipCheck
        ? null
        : problemFor(step, { agreed, ofAge, country, username: username.trim() });

      if (found) {
        setError(found);
        return;
      }

      setError(null);
      setGoingBack(false);
      setIndex((current) => current + 1);
    },
    [agreed, ofAge, country, username, step],
  );

  const goBack = useCallback(() => {
    setError(null);
    setGoingBack(true);
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  /**
   * Escape goes back. Enter continues, on the one step where nothing else owns
   * it.
   *
   * The flow engine binds Enter globally because half its steps are cards with
   * nothing focusable on them. Here most steps have their own owner: the
   * country filter takes Enter and picks the best remaining match, the username
   * field takes it and continues, the credentials form takes it and submits,
   * and the provider step has three buttons where Enter is a click. A window
   * handler on top of those would fire *as well* — a keydown listener runs
   * before the implicit form submission it cannot see coming — which on the
   * last step means submitting and advancing at once.
   *
   * So Enter is bound where it is genuinely unowned: the two checkboxes and a
   * button on the first step, and the country step in the state it is usually
   * in — one guessed answer and a Change beside it, with no field on screen to
   * take the key. Opening the list hands Enter back to the filter box, which is
   * why the condition asks whether the list is open rather than which step this
   * is.
   *
   * Escape rather than Backspace for going back, for the engine's reason:
   * Backspace is a character in every field on screen, and Escape already means
   * *out of this, back to where I was*.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || working) return;
      if (event.isComposing || event.keyCode === 229) return;

      if (event.key === 'Escape') {
        if (index === 0) return;
        event.preventDefault();
        goBack();
        return;
      }

      if (event.key !== 'Enter' || event.shiftKey || event.altKey) return;
      if (step !== 'consent' && !countryStepIsAnswered) return;

      /*
        A focused button or link already turns Enter into a click, and acting
        on top of that would answer the question and skip the next one.

        Checkboxes are deliberately not in this list, even though they are
        inputs: a checkbox does nothing with Enter, and after ticking the second
        box with Space the focus is sitting on exactly the element this would
        otherwise refuse to act from — which is to say, on the step that prints
        "Press Enter ↵", the key would do nothing. A field that genuinely owns
        Enter calls `preventDefault`, and that is caught above.
      */
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('button, a[href], summary, select')) return;

      event.preventDefault();
      advance();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [advance, countryStepIsAnswered, goBack, index, step, working]);

  /** Signing up with a provider, which ends the flow where it stands. */
  async function withProvider(id: string): Promise<void> {
    setBusy(id);
    setError(null);
    /*
      Whatever this does, it is not the email route.

      Only reachable by coming *back* to this question after picking email —
      and leaving the flag set would leave two questions on the strip that this
      route never asks, still there behind a provider that failed.
    */
    setByEmail(false);

    const result = await runProviderSignIn(id, `${window.location.origin}/library`);

    if (!result.ok) {
      // Already a sentence about what happened and what to do next.
      setError(result.message);
      setBusy(null);
      return;
    }

    /*
      Straight in. Discord, Twitch and Google each hand back an address they
      have verified themselves, and Better Auth writes `emailVerified` from
      what they say — so there is nothing left for this account to prove. That
      is the whole reason `socialProviders` in the API refuses a provider that
      cannot supply a verified address.
    */
    await finish(true);
  }

  /** The last step's submit: the account, then everything it declared. */
  async function withEmail(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    const form = new FormData(event.currentTarget);
    setBusy('account');
    setError(null);

    const result = await signUp.email({
      email: String(form.get('email') ?? ''),
      password: String(form.get('password') ?? ''),
      /*
       * The username, into Better Auth's `name`. That column is its contract
       * and cannot be renamed; what changed is the question we ask to fill it.
       * See `@goodbuild/rules/username`.
       */
      name: username.trim(),
      /**
       * Where the confirmation link comes back to, and it has to be absolute.
       * The link is built by the API and a relative path would resolve against
       * *its* origin — landing somebody on a JSON endpoint after the one click
       * the email asked them for.
       */
      callbackURL: `${window.location.origin}/verified`,
    });

    if (result.error) {
      setError(result.error.message ?? 'That did not work. Try again.');
      setBusy(null);
      return;
    }

    // Not in yet. The email is already on its way — `sendOnSignUp` — and the
    // wall is where somebody waits for it.
    await finish(false);
  }

  return (
    <AuthScreen
      title="New player"
      foot={
        <p>
          Already have an account? <Link href="/sign-in">Sign in</Link>.
        </p>
      }
    >
      <div className={styles.flow}>
        <div className={styles.progressHead}>
          <span className={styles.progressCount}>
            {index + 1} of {asked.length}
          </span>
          {index > 0 && (
            <button type="button" className={styles.back} onClick={goBack} disabled={working}>
              Back
              <kbd className={styles.backKey}>Esc</kbd>
            </button>
          )}
        </div>

        {/*
          The progress strip, which is the guidelines' signature device doing the
          job it already means, and the way back to any question behind you. A
          percentage bar would have been the one place in the product where
          progress is drawn in a way that means nothing.

          Forward is not offered: skipping ahead would walk past a required
          answer and land on a question whose button is dead for a reason two
          screens back, and there is nothing on a bar that could explain that.
        */}
        <div className={styles.progress} role="group" aria-label="Questions in this form">
          {asked.map((entry, position) => {
            const state = position < index ? 'done' : position === index ? 'current' : 'todo';

            return (
              <button
                key={entry}
                type="button"
                className={styles.progressStep}
                disabled={state !== 'done' || working}
                title={QUESTION[entry]}
                aria-label={`${position + 1} of ${asked.length}: ${QUESTION[entry]}`}
                {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}
                onClick={() => {
                  setError(null);
                  setGoingBack(true);
                  setIndex(position);
                }}
              >
                <i className={styles.progressBar} data-state={state} />
              </button>
            );
          })}
        </div>

        <div
          key={step}
          className={`${styles.step} ${goingBack ? styles.stepEnterBack : styles.stepEnter}`}
        >
          <h2 className={styles.question} id={headingId}>
            {QUESTION[step]}
          </h2>
          <p className={`gb-body ${styles.help}`}>
            {step === 'country' && countryIsOnlyAGuess ? COUNTRY_GUESS_HELP : HELP[step]}
          </p>

          {step === 'consent' && (
            <div style={column(4)}>
              <div className={ui.providers ?? ''}>
                <DocumentLink href="/legal/terms" label="Read the terms" />
                <DocumentLink href="/legal/privacy" label="Read the privacy policy" />
              </div>

              <Tick
                checked={agreed}
                onChange={setAgreed}
                label="I have read the terms and the privacy policy, and I agree to them."
              />
              <Tick checked={ofAge} onChange={setOfAge} label="I am 18 years of age or older." />
            </div>
          )}

          {step === 'country' &&
            (countryStepIsAnswered ? (
              <div style={column(3)}>
                <p className={styles.connected}>{countryLabel(country)}</p>
                <button
                  type="button"
                  className={styles.change}
                  disabled={working}
                  onClick={() => setCountryListOpen(true)}
                >
                  Change
                </button>
              </div>
            ) : (
              <FlowField
                step={COUNTRY_STEP}
                value={country}
                values={{}}
                invalid={error !== null}
                describedBy={headingId}
                onChange={pickCountry}
                onEnter={() => advance()}
                onPick={(picked) => {
                  pickCountry(picked);
                  advance(true);
                }}
              />
            ))}

          {step === 'method' && (
            <Ways
              available={offeredProviders}
              busy={busy}
              onEmail={() => {
                setByEmail(true);
                // The pick *is* the answer, as on the country list.
                advance(true);
              }}
              onProvider={withProvider}
            />
          )}

          {step === 'username' && (
            <FlowField
              step={USERNAME_STEP}
              value={username}
              values={{}}
              invalid={error !== null}
              describedBy={headingId}
              onChange={setUsername}
              onEnter={() => advance()}
              onPick={() => {}}
            />
          )}

          {step === 'credentials' && (
            <Credentials busy={busy} onSubmit={(event) => void withEmail(event)} />
          )}

          {error !== null && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          {busy !== null && busy !== 'account' && (
            <Loader size="sm" label="Waiting for the provider" />
          )}

          {step !== 'method' && step !== 'credentials' && (
            <div className={styles.controls}>
              <button
                type="button"
                className={styles.next}
                onClick={() => advance()}
                disabled={working || unanswered(step, { agreed, ofAge, country, username })}
              >
                Next
              </button>
              <span className={styles.enterHint}>Press Enter ↵</span>
            </div>
          )}
        </div>
      </div>
    </AuthScreen>
  );
}

/* -- The steps ----------------------------------------------------------- */

type StepName = 'consent' | 'country' | 'method' | 'username' | 'credentials';

/**
 * The two shapes this flow has.
 *
 * Written out rather than filtered from one list with a predicate, because
 * there are exactly two and naming them is shorter than the machinery that
 * would derive them. `PROVIDER_STEPS` is a prefix of `EMAIL_STEPS`, which is
 * what makes picking email at step three simply extend the strip rather than
 * renumber anything behind it.
 */
const PROVIDER_STEPS: readonly StepName[] = ['consent', 'country', 'method'];
const EMAIL_STEPS: readonly StepName[] = [...PROVIDER_STEPS, 'username', 'credentials'];

/**
 * The questions, set large, one sentence each in the second person.
 *
 * Four places print a question — the heading, the strip's tooltip, the strip's
 * `aria-label` and this table — so it is a table. Section 29 of the house
 * rules on copy: words a child knows, and half the length you first wrote.
 */
const QUESTION: Record<StepName, string> = {
  consent: 'First, the rules.',
  country: 'Where are you?',
  method: 'How do you want to sign up?',
  username: 'Pick a username.',
  credentials: 'Last bit.',
};

const HELP: Record<StepName, string> = {
  consent: 'Read both — they open in a new tab — then tick to agree.',
  country: 'The country you live in.',
  method: 'Any of these makes the same account. None of them is required.',
  username: 'What we call you. Letters and numbers, no spaces.',
  credentials: 'Your address and a password, on one screen so your password manager can save both.',
};

const COUNTRY_GUESS_HELP = 'We guessed from your connection. Change it if it is wrong.';

/**
 * Why the answer on screen will not do, or null when it will.
 *
 * A sentence, addressed to the person, shown under the question rather than
 * beside the field it is about — there is only one field on screen, so there is
 * nowhere else it could be about.
 */
function problemFor(
  step: StepName,
  answers: { agreed: boolean; ofAge: boolean; country: string; username: string },
): string | null {
  if (step === 'consent' && !(answers.agreed && answers.ofAge)) return 'Tick both to carry on.';
  if (step === 'country' && answers.country === '') return 'Pick where you are.';
  if (step === 'username') return usernameProblem(answers.username);
  return null;
}

/** Whether the question on screen still has no answer, for a dead Next button. */
function unanswered(
  step: StepName,
  answers: { agreed: boolean; ofAge: boolean; country: string; username: string },
): boolean {
  if (step === 'consent') return !(answers.agreed && answers.ofAge);
  if (step === 'country') return answers.country === '';
  if (step === 'username') return answers.username.trim() === '';
  return false;
}

/**
 * The country list, named in the reader's own language.
 *
 * `ISO_COUNTRY_CODES` is codes only — see the note on it for why this is the
 * whole ISO list and not the payout list beside it — so the names come from
 * `Intl.DisplayNames`, which every browser and both webviews have had for
 * years. A player in Poland gets `Niemcy`, one in Britain gets `Germany`, and
 * nobody maintains a translation table.
 *
 * Sorted with a collator rather than by `<`, because alphabetical order is a
 * property of a language and not of UTF-16: `Åland` sorts after `Austria` in
 * English and last of all in Swedish, and only the collator knows which reader
 * is looking.
 *
 * The whole thing is wrapped, and falls back to bare codes. `Intl.DisplayNames`
 * is not universal — a stripped-down ICU build has no region data at all — and
 * a country picker that throws is a sign-up that cannot be completed. A list of
 * two-letter codes is a bad list; it is not a broken page.
 */
function countryOptions(): readonly FlowChoiceOption[] {
  const plain = ISO_COUNTRY_CODES.map((code) => ({ value: code, label: code }));

  try {
    const names = new Intl.DisplayNames(undefined, { type: 'region' });
    const collator = new Intl.Collator();

    return ISO_COUNTRY_CODES.map((code) => ({ value: code, label: names.of(code) ?? code })).sort(
      (left, right) => collator.compare(left.label, right.label),
    );
  } catch {
    return plain;
  }
}

/**
 * The two steps `FlowField` renders, as the engine's own types.
 *
 * Module constants rather than built in the component, so the options array
 * keeps its identity across renders — `ChoiceField` memoises its filtering on
 * it, and a fresh array every keystroke would rebuild a 249-entry list on every
 * one of them.
 */
const COUNTRY_OPTIONS = countryOptions();

/**
 * The answer as the step prints it once it has one: the country in the
 * reader's own language, and the code that will be recorded.
 *
 * Both halves, because they are what the two audiences of this screen each
 * need. The name is what somebody checks the guess against; the code is what
 * `POST /v1/me/registration` stores, and printing it is how a wrong guess about
 * a country whose name they do not recognise is still catchable.
 */
function countryLabel(code: string): string {
  const named = COUNTRY_OPTIONS.find((option) => option.value === code);
  return named ? `${named.label} (${named.value})` : code;
}

const COUNTRY_STEP: FlowStep = {
  kind: 'choice',
  name: 'country',
  question: QUESTION.country,
  options: COUNTRY_OPTIONS,
  searchPlaceholder: 'Type a country',
  emptyHint: 'Nothing matches. Check the spelling.',
};

const USERNAME_STEP: FlowStep = {
  kind: 'text',
  name: 'username',
  question: QUESTION.username,
  placeholder: 'player-one',
  maxLength: USERNAME_MAX,
  autoCapitalize: 'none',
};

/* -- The parts of a step ------------------------------------------------- */

/**
 * One document, as a way to go and read it.
 *
 * **A new tab rather than the text inlined here**, and rather than an ordinary
 * link. Both alternatives were tried on this screen: the whole of both
 * documents printed above the boxes made the first thing anybody sees of
 * GOODBUILD a wall of clauses in a 26rem column, and a plain in-page link would
 * take somebody out of a form they are halfway through and drop them back at
 * the start of it.
 *
 * `target="_blank"` with `rel="noopener noreferrer"` — `noopener` because a
 * page opened with a handle on `window.opener` can navigate the tab that opened
 * it, and the tab that opened this one is a sign-up form.
 *
 * A plain `<a>` rather than the router's `Link`: a new tab is a fresh document
 * load either way, and this deliberately does not go through client-side
 * routing.
 */
function DocumentLink({ href, label }: { href: string; label: string }): React.ReactElement {
  return (
    <a className={ui.docLink ?? ''} href={href} target="_blank" rel="noopener noreferrer">
      {label}
      {/* Says it leaves. Decorative, so it is not announced twice. */}
      <span aria-hidden="true">↗</span>
    </a>
  );
}

/**
 * A box somebody ticks.
 *
 * The label is the control — the input is inside it — so the sentence is as
 * clickable as the box, which on a touch screen is the difference between a
 * target and a near miss. Deliberately no link inside the text: a click on one
 * would toggle the box on its way out of the page, which is the worst kind of
 * agreement to have recorded. The documents are above it instead.
 */
function Tick({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (next: boolean) => void;
}): React.ReactElement {
  return (
    <label className={ui.check ?? ''} data-checked={checked}>
      <input
        type="checkbox"
        className={ui.checkBox ?? ''}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={ui.checkText ?? ''}>{label}</span>
    </label>
  );
}

/**
 * The address and the password, on one screen and in one form.
 *
 * **That is the whole reason this step is two questions when every other step
 * is one.** A password manager offers to save a credential when it sees an
 * identifier and a password submitted together; asked on two screens, it saves
 * a password with nothing attached to it, and the person finds out the next
 * time they try to sign in.
 *
 * **No hidden username field beside them.** The credential this account signs
 * in with is the address. A manager taught to fill `player-one` into the email
 * box would be filling in the wrong thing every time from then on, which is a
 * worse failure than the one it would be trying to prevent.
 *
 * Its own component so the address can take focus on arrival, the way every
 * other step's field does — a `useEffect` and a ref inside the route would have
 * to run on every step to be honest about its dependencies.
 */
function Credentials({
  busy,
  onSubmit,
}: {
  busy: string | null;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}): React.ReactElement {
  const address = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    address.current?.focus();
  }, []);

  return (
    <form className={ui.stack ?? ''} onSubmit={onSubmit}>
      <Field label="Email">
        <input
          ref={address}
          className={ui.input ?? ''}
          name="email"
          type="email"
          required
          autoComplete="email"
        />
      </Field>

      <Field label="Password">
        <input
          className={ui.input ?? ''}
          name="password"
          type="password"
          required
          minLength={8}
          autoComplete="new-password"
        />
      </Field>

      <button type="submit" className={styles.next} disabled={busy !== null}>
        {busy === 'account' ? 'Making your account…' : 'Create account'}
      </button>
    </form>
  );
}

/** An envelope, so the email route reads as one of the buttons and not as a link. */
const ENVELOPE = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h17A1.5 1.5 0 0 1 22 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 18.5v-13Zm2.2.5 7.8 6.1L19.8 6H4.2ZM20 7.6l-7.4 5.8a1 1 0 0 1-1.2 0L4 7.6V18h16V7.6Z" />
  </svg>
);

/**
 * Every way in, as a list.
 *
 * **Email first, then the providers.** Hard rule 22 says no social account is
 * ever required to buy a game, and the sign-in page keeps the email form above
 * these buttons for the same reason — putting them first would make the email
 * route read as the fallback rather than as the equal it is. Here they are all
 * buttons in one list, so the order is the only thing left saying which is
 * which.
 *
 * A deployment with no provider credentials draws one button and no rule above
 * it, and the step still works. That is a fresh clone's state.
 */
function Ways({
  available,
  busy,
  onEmail,
  onProvider,
}: {
  available: string[] | null;
  busy: string | null;
  onEmail: () => void;
  onProvider: (id: string) => Promise<void>;
}): React.ReactElement {
  // Null while unknown, so three buttons do not appear a beat later and push
  // the one above them up the page under somebody's cursor.
  const offered = available === null ? [] : PROVIDERS.filter((one) => available.includes(one.id));

  return (
    <div style={column(3)}>
      <div className={ui.providers ?? ''}>
        <button
          type="button"
          className={ui.provider ?? ''}
          disabled={busy !== null}
          onClick={onEmail}
        >
          {ENVELOPE}
          With an email address
        </button>
      </div>

      {available === null && <Loader size="sm" label="Looking for the other ways in" />}

      {offered.length > 0 && (
        <>
          <p className={ui.orRule ?? ''}>or</p>

          <div className={ui.providers ?? ''}>
            {offered.map((provider) => (
              <button
                key={provider.id}
                type="button"
                className={ui.provider ?? ''}
                disabled={busy !== null}
                onClick={() => void onProvider(provider.id)}
              >
                {provider.icon}
                {busy === provider.id
                  ? 'One moment…'
                  : provider.label.replace('Continue', 'Sign up')}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
